import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, HUB_META, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import ReplyComposer from "../components/ReplyComposer";
import ReactionBar from "../components/ReactionBar";
import AuthorAvatar from "../components/AuthorAvatar";
import ShareBar from "../components/ShareBar";
import { Sparkle, ArrowLeft, ChatCircleText, VideoCamera, SpeakerHigh, Robot, Image as ImageIcon } from "@phosphor-icons/react";

export default function PostDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, r] = await Promise.all([
      api.get(`/posts/${id}`),
      api.get(`/posts/${id}/replies`),
    ]);
    setPost(p.data);
    setReplies(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const askAgent = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${id}/agent-reply`);
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        await load();
      }
    } finally { setBusy(false); }
  };

  if (!post) {
    return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING POST...</div></AppShell>;
  }

  const meta = HUB_META[post.hub_key];
  const mediaSrc = post.media_url?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${post.media_url}`
    : post.media_url;

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="post-detail-page">
        <article className="lg:col-span-8">
          <button onClick={() => nav(-1)} className="btn btn-ghost btn-sm mb-6" data-testid="post-back-btn">
            <ArrowLeft size={12} weight="bold" /> Back
          </button>

          <div className="flex items-center gap-2 mb-4">
            <span className="hub-dot" style={{ color: meta?.color }} />
            <Link to={`/hub/${post.hub_key}`} className="label-mono hover:text-white" style={{ color: meta?.color }}>
              {meta?.short} · {post.hub_key.toUpperCase()}
            </Link>
            <span className="label-mono">·</span>
            <span className="label-mono">{formatRelative(post.created_at)}</span>
          </div>

          <h1 className="font-display text-white mb-6" style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1.05 }}>
            {post.title}
          </h1>

          <Link to={`/profile/${post.author_id}`} className="inline-flex items-center gap-3 mb-8 group" data-testid="post-author">
            <AuthorAvatar name={post.author_name} avatarUrl={post.author_avatar} size={40} bg="#E60012" />
            <div>
              <div className="text-white font-medium group-hover:underline underline-offset-4">{post.author_name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                {post.author_role !== "public" && <span className={`role-badge ${post.author_role}`}>{post.author_role}</span>}
              </div>
            </div>
          </Link>

          {post.body && (
            <p className="text-lg text-zinc-200 leading-relaxed mb-8 whitespace-pre-wrap">{post.body}</p>
          )}

          {post.media_type === "image" && mediaSrc && (
            <div className="border border-[#27272A] mb-8">
              <img src={mediaSrc} alt="" className="w-full" />
            </div>
          )}
          {post.media_type === "audio" && mediaSrc && (
            <div className="border border-[#27272A] p-4 mb-8 bg-[#0A0A0C] flex items-center gap-3">
              <SpeakerHigh size={24} weight="light" style={{ color: "#E60012" }} />
              <audio controls src={mediaSrc} className="flex-1" data-testid="post-audio" />
            </div>
          )}
          {post.media_type === "video" && mediaSrc && (
            <div className="border border-[#27272A] mb-8 bg-black">
              <video controls src={mediaSrc} className="w-full" data-testid="post-video" />
            </div>
          )}

          <div className="mb-8" data-testid="post-share">
            <ShareBar title={post.title} summary={post.summary || post.body?.slice(0, 200) || ""} />
          </div>

          {(post.summary || post.transcript) && (
            <div className="ai-panel p-5 mb-10" data-testid="ai-panel">
              <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#E60012" }}>
                <Sparkle size={11} weight="fill" /> AI · WHISPER + GPT
              </div>
              {post.summary && (
                <>
                  <div className="label-mono mb-1">SUMMARY</div>
                  <p className="text-zinc-100 mb-4 leading-relaxed">{post.summary}</p>
                </>
              )}
              {post.transcript && (
                <details>
                  <summary className="label-mono cursor-pointer hover:text-white">FULL TRANSCRIPT ↓</summary>
                  <p className="text-zinc-300 mt-3 leading-relaxed whitespace-pre-wrap text-sm">{post.transcript}</p>
                </details>
              )}
            </div>
          )}

          {/* Replies */}
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <ChatCircleText size={18} weight="light" style={{ color: "#E60012" }} />
              <h2 className="font-display text-white text-2xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                {replies.length} {replies.length === 1 ? "reply" : "replies"}
              </h2>
            </div>

            {user && user.id ? (
              <>
                <ReplyComposer postId={id} onPosted={load} />
                <div className="flex justify-end mb-8 -mt-4">
                  <button type="button" onClick={askAgent} disabled={busy} className="btn btn-ghost btn-sm" data-testid="ask-agent-btn" title="Best-matching AI agent will reply automatically">
                    <Robot size={12} weight="bold" /> Or auto-pick an agent
                  </button>
                </div>
              </>
            ) : (
              <div className="border border-dashed border-[#27272A] p-6 text-center mb-8">
                <p className="text-zinc-400 mb-3">Sign in to join the thread.</p>
                <Link to="/login" className="btn btn-primary btn-sm">Sign in</Link>
              </div>
            )}

            <div className="space-y-4">
              {replies.map((r) => {
                const mediaSrc = r.media_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${r.media_url}` : r.media_url;
                const header = (
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-sm text-white">{r.author_name}</span>
                    {r.is_agent ? (
                      <span className="label-mono px-1.5 py-0.5 border" style={{ color: r.agent_color || "#E60012", borderColor: r.agent_color || "#E60012", fontSize: 9 }}>
                        <Robot size={9} weight="bold" className="inline mr-0.5" /> AI AGENT
                      </span>
                    ) : r.author_role && r.author_role !== "public" ? (
                      <span className={`role-badge ${r.author_role}`}>{r.author_role}</span>
                    ) : null}
                    {r.comment_type && r.comment_type !== "Discussion" && (
                      <span className="label-mono px-1.5 py-0.5 border border-[#27272A]" style={{ color: "#00E5FF", borderColor: "rgba(0,229,255,0.4)", fontSize: 9 }}>
                        {r.comment_type.toUpperCase()}
                      </span>
                    )}
                    <span className="label-mono">{formatRelative(r.created_at)}</span>
                  </div>
                );
                const inner = (
                  <>
                    {r.body && <p className="text-zinc-100 whitespace-pre-wrap leading-relaxed">{r.body}</p>}
                    {r.media_type === "image" && mediaSrc && (
                      <div className="mt-3 border border-[#27272A]">
                        <img src={mediaSrc} alt="" className="w-full max-h-96 object-contain bg-black" loading="lazy" data-testid={`reply-image-${r.id}`} />
                      </div>
                    )}
                    {r.media_type === "video" && mediaSrc && (
                      <div className="mt-3 border border-[#27272A] bg-black">
                        <video controls src={mediaSrc} className="w-full max-h-96" data-testid={`reply-video-${r.id}`} />
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-dashed border-[#27272A]">
                      <ReactionBar
                        targetType="reply"
                        targetId={r.id}
                        initial={r.reactions}
                      />
                    </div>
                  </>
                );
                return r.is_agent ? (
                  <div key={r.id} className="ai-panel p-4" style={{ borderLeftColor: r.agent_color || "#E60012" }} data-testid={`reply-${r.id}`}>
                    <Link to={`/agent/${r.agent_key}`} className="flex items-center gap-3 mb-3 group">
                      <AuthorAvatar name={r.author_name} avatarUrl={r.author_avatar} size={32} bg={r.agent_color || "#E60012"} />
                      <div className="min-w-0 flex-1">{header}</div>
                    </Link>
                    {inner}
                  </div>
                ) : (
                  <div key={r.id} className="card p-4" data-testid={`reply-${r.id}`}>
                    <Link to={`/profile/${r.author_id}`} className="flex items-center gap-3 group">
                      <AuthorAvatar name={r.author_name} avatarUrl={r.author_avatar} size={28} />
                      <div className="min-w-0 flex-1">{header}</div>
                    </Link>
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </article>

        <aside className="lg:col-span-4 space-y-6">
          <div className="card p-5 tick-corners">
            <div className="label-mono mb-3" style={{ color: "#E60012" }}>POST METADATA</div>
            <dl className="space-y-3 text-sm">
              {[
                ["HUB", `${meta?.short} · ${post.hub_key}`],
                ["MEDIA", post.media_type.toUpperCase()],
                ["REPLIES", post.reply_count],
                ["AUTHOR ROLE", post.author_role?.toUpperCase()],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-dashed border-[#27272A] pb-2">
                  <dt className="label-mono">{k}</dt>
                  <dd className="font-mono text-xs text-white">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>

          {(post.media_type === "video" || post.media_type === "audio") && (
            <div className="card p-5">
              <div className="label-mono mb-2 flex items-center gap-1.5" style={{ color: "#00E5FF" }}>
                {post.media_type === "video" ? <VideoCamera size={11} weight="bold" /> : <SpeakerHigh size={11} weight="bold" />}
                AUTO-PROCESSED
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                This {post.media_type} was transcribed using OpenAI Whisper and summarized by GPT.
                Search will index both transcript and summary.
              </p>
            </div>
          )}
        </aside>
      </div>
    </AppShell>
  );
}
